/**
 * Simple Dashboard Page - Uses Auth Data Directly
 * No additional API calls, cart data comes from auth service
 * Enhanced with employee preview mode (company picker)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency, formatPhoneNumber } from '../../utils/formatters';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const DashboardPage = () => {
  const navigate = useNavigate();

  const {
    user,
    store: currentStore,
    company: currentCompany,
    availableStores,
    needsStoreSelection,
    canProceedToShopping,
    getUserDisplayName,
    getStoreDisplayName,
    isLoading,
    selectStore,
    switchCompany,
    isEmployee,
    logout
  } = useAuth();

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [selectedStoreName, setSelectedStoreName] = useState('');

  const isEmp = isEmployee();
  const companies = user?.companies || [];

  // Auto-redirect after successful selection
  useEffect(() => {
    if (showSuccessOverlay) {
      const timer = setTimeout(() => {
        setShowSuccessOverlay(false);
        navigate('/', { replace: true });
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [showSuccessOverlay, navigate]);

  // Handle store selection (customers)
  const handleStoreSelect = async (store) => {
    if (isSelecting) return;

    setIsSelecting(true);
    setSelectedStoreId(store.id);
    setSelectedStoreName(store.name);

    try {
      await selectStore(store);
      setShowSuccessOverlay(true);
    } catch (error) {
      console.error('Failed to select store:', error);
      alert('Failed to select store. Please try again.');
    } finally {
      setIsSelecting(false);
      setSelectedStoreId(null);
    }
  };

  // Handle company selection (employees)
  const handleCompanySelect = async (company) => {
    if (isSelecting) return;

    setIsSelecting(true);
    setSelectedStoreName(company.displayName || company.companyName);

    try {
      await switchCompany({
        id: company.companyId,
        name: company.companyName,
        slug: company.slug,
        displayName: company.displayName,
        isMed: company.isMed || false,
        isRec: company.isRec || false,
        allowCustomCaseUnitLabels: company.allowCustomCaseUnitLabels || false,
      });
      setShowSuccessOverlay(true);
    } catch (error) {
      console.error('Failed to select company:', error);
      alert('Failed to select company. Please try again.');
    } finally {
      setIsSelecting(false);
    }
  };

  // Handle quick order - select store and navigate (customers only)
  const handleQuickOrder = async (store) => {
    if (isSelecting) return;

    if (currentStore?.id !== store.id) {
      setIsSelecting(true);
      setSelectedStoreId(store.id);

      try {
        await selectStore(store);
        navigate('/quick-order');
      } catch (error) {
        console.error('Failed to select store for quick order:', error);
        alert('Failed to select store. Please try again.');
      } finally {
        setIsSelecting(false);
        setSelectedStoreId(null);
      }
    } else {
      navigate('/quick-order');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container-fluid py-5">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-3">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // No access — employee with no companies
  if (isEmp && companies.length === 0) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card border-0 shadow-lg">
              <div className="card-body p-5 text-center">
                <div className="mb-4">
                  <i className="bi bi-building-exclamation text-muted" style={{ fontSize: '3rem' }}></i>
                </div>
                <h3 className="card-title mb-3">No Company Access</h3>
                <p className="card-text text-muted mb-4">
                  Your employee account doesn't have access to any companies yet. Please contact your administrator.
                </p>
                <div className="d-flex gap-3 justify-content-center">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => window.location.reload()}>
                    <i className="bi bi-arrow-clockwise me-2"></i>Refresh
                  </button>
                  <button type="button" className="btn btn-outline-danger" onClick={() => { logout(); navigate('/login'); }}>
                    <i className="bi bi-box-arrow-left me-2"></i>Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No access — customer with no stores
  if (!isEmp && (!availableStores || availableStores.length === 0)) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card border-0 shadow-lg">
              <div className="card-body p-5 text-center">
                <div className="mb-4">
                  <i className="bi bi-shop text-muted" style={{ fontSize: '3rem' }}></i>
                </div>
                <h3 className="card-title mb-3">No Store Access</h3>
                <p className="card-text text-muted mb-4">
                  Your account doesn't have access to any stores yet. Please contact your administrator to get store access assigned.
                </p>
                <div className="d-flex gap-3 justify-content-center">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => window.location.reload()}>
                    <i className="bi bi-arrow-clockwise me-2"></i>Refresh
                  </button>
                  <button type="button" className="btn btn-outline-danger" onClick={() => { logout(); navigate('/login'); }}>
                    <i className="bi bi-box-arrow-left me-2"></i>Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h1 className="h3 mb-2">My Account</h1>
                <p className="text-muted mb-0">
                  Welcome back, {getUserDisplayName()}!
                </p>
              </div>

              {isEmp ? (
                currentCompany && (
                  <div className="text-end">
                    <div className="small text-muted">Current company:</div>
                    <div className="fw-semibold text-info">
                      <i className="bi bi-eye-fill text-info me-1"></i>
                      {currentCompany.displayName || currentCompany.name}
                    </div>
                  </div>
                )
              ) : (
                currentStore && (
                  <div className="text-end">
                    <div className="small text-muted">Current store:</div>
                    <div className="fw-semibold text-success">
                      <i className="bi bi-check-circle-fill text-success me-1"></i>
                      {getStoreDisplayName()}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Employee Preview Mode */}
        {isEmp && (
          <>
            {/* Preview Mode Banner */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="alert alert-info d-flex align-items-center mb-0">
                  <i className="bi bi-eye-fill me-2 fs-4"></i>
                  <div>
                    <strong>Employee Preview Mode</strong>
                    <span className="ms-2 text-muted">
                      You are browsing as an employee. Cart, checkout, and order features are disabled.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Quick Actions - Browse only */}
            {canProceedToShopping() && (
              <div className="row g-4 mb-4">
                <div className="col-lg-4 col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="bi bi-grid text-primary" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="card-title">Browse Products</h5>
                      <p className="card-text text-muted small">View the full product catalog</p>
                      <button className="btn btn-primary btn-sm" onClick={() => navigate('/')}>
                        View Products
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="bi bi-tags text-success" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="card-title">Browse Categories</h5>
                      <p className="card-text text-muted small">Explore products by category</p>
                      <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/categories')}>
                        View Categories
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="bi bi-award text-warning" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="card-title">Browse Brands</h5>
                      <p className="card-text text-muted small">Explore products by brand</p>
                      <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/brands')}>
                        View Brands
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Company Picker */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5 className="card-title mb-0">
                        <i className="bi bi-building text-primary me-2"></i>
                        {companies.length > 1 ? 'Select Company to Preview' : 'Your Company'}
                      </h5>
                      <span className="badge bg-primary">
                        {companies.length} company{companies.length !== 1 ? 'ies' : 'y'}
                      </span>
                    </div>

                    {companies.length > 1 && !currentCompany && (
                      <p className="text-muted mb-4">
                        Please select a company to preview its storefront. You can switch companies anytime.
                      </p>
                    )}

                    <div className="row g-3">
                      {companies.map((company) => {
                        const isCurrentCompany = currentCompany?.id === company.companyId;

                        return (
                          <div key={company.companyId} className="col-md-6 col-lg-4">
                            <div
                              className={`card h-100 ${
                                isCurrentCompany ? 'border-success bg-success bg-opacity-10' :
                                !isSelecting ? 'border-light store-card-selectable' : 'border-light'
                              }`}
                              style={{
                                cursor: isCurrentCompany || isSelecting ? 'default' : 'pointer',
                                transition: 'all 0.2s ease',
                                opacity: isSelecting && !isCurrentCompany ? 0.6 : 1
                              }}
                              onClick={() => !isCurrentCompany && !isSelecting && handleCompanySelect(company)}
                            >
                              <div className="card-body p-3">
                                <div className="d-flex align-items-start">
                                  <div className="me-3 mt-1">
                                    {isCurrentCompany ? (
                                      <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '1.25rem' }}></i>
                                    ) : (
                                      <i className="bi bi-building text-primary" style={{ fontSize: '1.25rem' }}></i>
                                    )}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                      <h6 className="fw-bold mb-0">
                                        {company.displayName || company.companyName}
                                      </h6>
                                      {isCurrentCompany && (
                                        <span className="badge bg-success small">Current</span>
                                      )}
                                    </div>
                                    {company.dbaName && (
                                      <div className="text-muted small mb-1">DBA: {company.dbaName}</div>
                                    )}

                                    {/* Action buttons */}
                                    <div className="mt-2">
                                      {isCurrentCompany ? (
                                        <button
                                          className="btn btn-primary btn-sm"
                                          onClick={(e) => { e.stopPropagation(); navigate('/'); }}
                                        >
                                          <i className="bi bi-eye me-1"></i>Preview Products
                                        </button>
                                      ) : (
                                        <button
                                          className="btn btn-outline-primary btn-sm"
                                          onClick={(e) => { e.stopPropagation(); handleCompanySelect(company); }}
                                          disabled={isSelecting}
                                        >
                                          <i className="bi bi-arrow-right me-1"></i>Select Company
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {companies.length > 1 && currentCompany && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <div className="d-flex align-items-center text-muted">
                          <i className="bi bi-info-circle me-2"></i>
                          <small>Click on any company above to switch your preview.</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Customer Dashboard */}
        {!isEmp && (
          <>
            {/* Quick Actions - Only show if store is selected */}
            {canProceedToShopping() && (
              <div className="row g-4 mb-4">
                <div className="col-lg-3 col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="bi bi-cart-plus text-primary" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="card-title">Start Shopping</h5>
                      <p className="card-text text-muted small">Browse products and add them to your cart</p>
                      <button className="btn btn-primary btn-sm" onClick={() => navigate('/')}>Shop Now</button>
                    </div>
                  </div>
                </div>

                <div className="col-lg-3 col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="bi bi-lightning text-warning" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="card-title">Quick Order</h5>
                      <p className="card-text text-muted small">Order by product codes or names</p>
                      <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/quick-order')}>Quick Order</button>
                    </div>
                  </div>
                </div>

                <div className="col-lg-3 col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="bi bi-clock-history text-info" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="card-title">Order History</h5>
                      <p className="card-text text-muted small">View your past orders and reorder items</p>
                      <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/orders')}>View Orders</button>
                    </div>
                  </div>
                </div>

                <div className="col-lg-3 col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <div className="mb-3">
                        <i className="bi bi-person-gear text-secondary" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h5 className="card-title">Profile</h5>
                      <p className="card-text text-muted small">Update your account information</p>
                      <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/account/profile')}>Edit Profile</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Store Selection Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5 className="card-title mb-0">
                        <i className="bi bi-buildings text-primary me-2"></i>
                        {needsStoreSelection() ? 'Select Your Store' : 'Your Store Access'}
                      </h5>
                      <span className="badge bg-primary">{availableStores.length} store{availableStores.length !== 1 ? 's' : ''}</span>
                    </div>

                    {needsStoreSelection() && (
                      <p className="text-muted mb-4">
                        Please select a store to begin shopping. You can change stores anytime from this page.
                      </p>
                    )}

                    <div className="row g-3">
                      {availableStores.map((store) => {
                        const isCurrentStore = currentStore?.id === store.id;
                        const isLoadingThis = selectedStoreId === store.id;
                        const canSelect = !isCurrentStore && !isSelecting;

                        return (
                          <div key={store.id} className="col-md-6 col-lg-4">
                            <div
                              className={`card h-100 ${
                                isCurrentStore ? 'border-success bg-success bg-opacity-10' :
                                canSelect ? 'border-light store-card-selectable' : 'border-light'
                              }`}
                              style={{
                                cursor: canSelect ? 'pointer' : 'default',
                                transition: 'all 0.2s ease',
                                opacity: isSelecting && !isLoadingThis ? 0.6 : 1
                              }}
                              onClick={() => canSelect && handleStoreSelect(store)}
                            >
                              <div className="card-body p-3">
                                <div className="d-flex align-items-start">
                                  <div className="me-3 mt-1">
                                    {isCurrentStore ? (
                                      <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '1.25rem' }}></i>
                                    ) : isLoadingThis ? (
                                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Selecting...</span>
                                      </div>
                                    ) : (
                                      <i className="bi bi-shop text-primary" style={{ fontSize: '1.25rem' }}></i>
                                    )}
                                  </div>

                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                      <h6 className="fw-bold mb-0">{store.name}</h6>
                                      {isCurrentStore && (
                                        <span className="badge bg-success small">Current</span>
                                      )}
                                    </div>

                                    <div className="text-muted small">
                                      <div className="d-flex align-items-center mb-1">
                                        <i className="bi bi-geo-alt me-1" style={{ fontSize: '0.75rem' }}></i>
                                        <span>{store.address}</span>
                                      </div>
                                      <div className="d-flex align-items-center mb-1">
                                        <i className="bi bi-pin-map me-1" style={{ fontSize: '0.75rem' }}></i>
                                        <span>{store.city}</span>
                                      </div>
                                      {store.phone && (
                                        <div className="d-flex align-items-center">
                                          <i className="bi bi-telephone me-1" style={{ fontSize: '0.75rem' }}></i>
                                          <span>{formatPhoneNumber(store.phone)}</span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Cart Total Display */}
                                    {store.hasCartItems && (
                                      <div className="mt-2 p-2 bg-primary bg-opacity-10 rounded">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <small className="text-muted fw-semibold">Cart Total:</small>
                                          <strong className="text-primary">{formatCurrency(store.cartTotal)}</strong>
                                        </div>
                                        <small className="text-muted">
                                          {store.cartItemCount} item{store.cartItemCount !== 1 ? 's' : ''}
                                          ({store.cartUnitCount} unit{store.cartUnitCount !== 1 ? 's' : ''})
                                        </small>
                                        {store.cartFees > 0 && (
                                          <div className="small text-muted">
                                            Subtotal: {formatCurrency(store.cartSubtotal)} + {formatCurrency(store.cartFees)} fees
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Action buttons */}
                                    <div className="mt-3">
                                      {isCurrentStore ? (
                                        <div className="d-flex gap-2">
                                          <button
                                            className="btn btn-primary btn-sm flex-fill"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate(store.hasCartItems ? '/checkout' : '/');
                                            }}
                                          >
                                            <i className={`bi ${store.hasCartItems ? 'bi-cart-check' : 'bi-cart'} me-1`}></i>
                                            {store.hasCartItems ? 'View Cart' : 'Shop Now'}
                                          </button>
                                          <button
                                            className="btn btn-stash-accent btn-sm"
                                            onClick={(e) => { e.stopPropagation(); handleQuickOrder(store); }}
                                            disabled={isSelecting}
                                            title="Quick Order"
                                          >
                                            <i className="bi bi-lightning"></i> Quick Order
                                          </button>
                                        </div>
                                      ) : isLoadingThis ? (
                                        <div className="text-primary small">
                                          <i className="bi bi-arrow-repeat me-1"></i>
                                          Selecting store...
                                        </div>
                                      ) : (
                                        <div className="d-flex gap-2">
                                          <button
                                            className="btn btn-outline-primary btn-sm flex-fill"
                                            onClick={(e) => { e.stopPropagation(); handleStoreSelect(store); }}
                                            disabled={isSelecting}
                                          >
                                            <i className="bi bi-arrow-right me-1"></i>Select Store
                                          </button>
                                          <button
                                            className="btn btn-stash-accent btn-sm"
                                            onClick={(e) => { e.stopPropagation(); handleQuickOrder(store); }}
                                            disabled={isSelecting}
                                            title="Quick Order - Select this store and go to Quick Order"
                                          >
                                            <i className="bi bi-lightning"></i> Quick Order
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {!needsStoreSelection() && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <div className="d-flex align-items-center text-muted">
                          <i className="bi bi-info-circle me-2"></i>
                          <small>Click on any store above to switch to it.</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Success Overlay */}
      {showSuccessOverlay && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
             style={{
               backgroundColor: 'rgba(0, 0, 0, 0.8)',
               zIndex: 9999,
               backdropFilter: 'blur(4px)'
             }}>
          <div className="card border-0 shadow-lg" style={{ maxWidth: '400px' }}>
            <div className="card-body text-center p-5">
              <div className="mb-4">
                <div className="rounded-circle bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center mx-auto"
                     style={{ width: '80px', height: '80px' }}>
                  <i className={`bi ${isEmp ? 'bi-eye-fill text-info' : 'bi-check-circle-fill text-success'}`} style={{ fontSize: '2.5rem' }}></i>
                </div>
              </div>

              <h3 className={`${isEmp ? 'text-info' : 'text-success'} fw-bold mb-3`}>
                {isEmp ? 'Company Selected!' : 'Store Selected!'}
              </h3>
              <p className="text-muted mb-4">
                {isEmp
                  ? <>Now previewing <strong>{selectedStoreName}</strong></>
                  : <>You've successfully selected <strong>{selectedStoreName}</strong></>
                }
              </p>

              <div className="d-flex align-items-center justify-content-center text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <small>{isEmp ? 'Loading products...' : 'Redirecting to homepage...'}</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS styles */}
      <style>{`
        .store-card-selectable:hover {
          border-color: var(--bs-primary) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
          transform: translateY(-2px);
        }

        .store-card-selectable {
          transition: all 0.2s ease;
        }
      `}</style>
    </>
  );
};

export default DashboardPage;